import cn from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { MdClose, MdFileDownload, MdOutlineFileDownload } from 'react-icons/md';
import { AlexaVisibilityResult } from '@blisspointmedia/bpm-types/dist/AlexaVisibility';
import { Button } from '@Components/Button';
import { DatePicker } from '@Components/DatePicker/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { Appearance, Design, Size } from '@Components/designConstants';
import { EmptyState } from '@Components/EmptyState';
import { IconButton } from '@Components/IconButton';
import { downloadPNG } from '@src/utils/download-utils';
import { EMPTY_DATE_RANGE } from '../alexa-visibility-constants';
import { getMatrixBrands } from '../alexa-visibility-competitive-utils';
import {
  getMonthOverMonth,
  getQueryChanges,
  getSoarByDate,
  getTopProductsByBrand,
  getTrendWindows,
  getVisibilityByDate
} from '../alexa-visibility-trend-utils';
import { filterResultsByDateRange } from '../alexa-visibility-utils';
import { useBrandColor } from '../hooks/useBrandColor';
import { usePrintView } from '../hooks/usePrintView';
import { QueryMultiSelect } from './QueryMultiSelect';
import { ProductTrendsSection } from './trends/ProductTrendsSection';
import { TrendCard } from './trends/TrendCard';
import { TrendRecap } from './trends/TrendRecap';

interface TrendsTabProps {
  // The page's results, already narrowed by the page-level date filter.
  results: AlexaVisibilityResult[];
  // Every result, before any date filter. The tab's own date filter starts from these, so it
  // overrides the page-level one.
  allResults: AlexaVisibilityResult[];
  targetBrand: string;
}

const TAB_CLASS = 'trends-tab';
const PRINT_PAGE_STYLE = '@page { size: landscape; margin: 0.5in; }';
// The charts are drawn at the on-screen width, so while printing the page is squeezed to the
// printable width and the dialog waits for them to redraw.
const PRINT_DELAY_MS = 900;

export const TrendsTab = ({ results, allResults, targetBrand }: TrendsTabProps) => {
  // Empty means every query.
  const [selectedQueries, setSelectedQueries] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>(EMPTY_DATE_RANGE);
  const [isPrinting, setIsPrinting] = useState(false);

  const hasTabDateRange = Boolean(dateRange.start || dateRange.end);
  // The tab's own date filter replaces the page's when it is set.
  const dateScoped = useMemo(
    () =>
      hasTabDateRange
        ? filterResultsByDateRange(allResults, dateRange.start, dateRange.end)
        : results,
    [allResults, dateRange, hasTabDateRange, results]
  );

  const queryOptions = useMemo(
    () =>
      Array.from(new Set(dateScoped.map(result => result.query))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [dateScoped]
  );
  const activeQueries = useMemo(
    () => selectedQueries.filter(query => queryOptions.includes(query)),
    [queryOptions, selectedQueries]
  );
  const scoped = useMemo(
    () =>
      activeQueries.length === 0
        ? dateScoped
        : dateScoped.filter(result => activeQueries.includes(result.query)),
    [activeQueries, dateScoped]
  );

  const getBrandColor = useBrandColor(allResults, targetBrand);
  const brands = useMemo(() => getMatrixBrands(scoped, targetBrand), [scoped, targetBrand]);
  const brandSeries = useMemo(
    () => brands.map(brand => ({ key: brand, label: brand, color: getBrandColor(brand) })),
    [brands, getBrandColor]
  );

  const visibilityRows = useMemo(
    () => getVisibilityByDate(scoped, brands, targetBrand),
    [brands, scoped, targetBrand]
  );
  const soarRows = useMemo(() => getSoarByDate(scoped, brands), [brands, scoped]);
  const productGroups = useMemo(() => getTopProductsByBrand(scoped, brands), [brands, scoped]);

  const windows = useMemo(() => getTrendWindows(scoped), [scoped]);
  const monthOverMonth = useMemo(
    () => getMonthOverMonth(windows, targetBrand),
    [targetBrand, windows]
  );
  const { improvements, declines } = useMemo(() => getQueryChanges(windows), [windows]);

  const closePrint = useCallback(() => setIsPrinting(false), []);
  usePrintView(isPrinting, closePrint, PRINT_PAGE_STYLE, PRINT_DELAY_MS);

  const handleDownloadPng = (id: string) => {
    downloadPNG(
      `#${id}`,
      `AlexaVisibility_Trends_${id}_${targetBrand}`,
      'trends',
      ['no-print'],
      false
    );
  };

  if (allResults.length === 0) {
    return (
      <EmptyState
        title='No history yet'
        subtitle='Trends across runs will appear here once more than one scrape has completed.'
      />
    );
  }

  const dateCount = new Set(visibilityRows.map(row => row.date)).size;

  return (
    <div id={TAB_CLASS} className={cn(TAB_CLASS, { 'is-printing': isPrinting })}>
      <div className='aars-tab-actions'>
        <div className='aars-tab-header'>
          <div className='aars-tab-title'>Trends</div>
          <div className='aars-tab-description'>
            Track how {targetBrand}&apos;s Alexa for Shopping visibility is changing over time —
            spot improvements and identify where optimization is working.
          </div>
        </div>
        <div className='aars-export-buttons no-print'>
          <Button
            size={Size.SM}
            design={Design.NEUTRAL}
            leadingElement={<MdOutlineFileDownload />}
            onClick={() => setIsPrinting(true)}
          >
            PDF
          </Button>
          <Button
            size={Size.SM}
            design={Design.NEUTRAL}
            leadingElement={<MdFileDownload />}
            onClick={() => handleDownloadPng(TAB_CLASS)}
          >
            PNG
          </Button>
        </div>
      </div>

      <div className='aars-cc-filters no-print'>
        <QueryMultiSelect
          options={queryOptions}
          selected={activeQueries}
          onChange={setSelectedQueries}
        />
        <div className='aars-date-filter'>
          <DatePicker
            range={hasTabDateRange ? dateRange : undefined}
            placeholder='All dates'
            onChange={({ start, end }) => setDateRange({ start, end })}
          />
          {hasTabDateRange && (
            <IconButton
              appearance={Appearance.EMPTY}
              aria-label='Clear date filter'
              onClick={() => setDateRange(EMPTY_DATE_RANGE)}
            >
              <MdClose />
            </IconButton>
          )}
        </div>
        <span className='aars-cc-filter-count'>
          {dateCount} dates · {scoped.length.toLocaleString()} results
        </span>
      </div>

      {scoped.length === 0 ? (
        <EmptyState
          title='No results match these filters'
          subtitle='Choose different queries or dates to see trends.'
        />
      ) : (
        <div className='aars-trend-sections'>
          <TrendCard
            id='trend-visibility'
            title='Visibility score by date'
            subtitle={`% of Alexa responses each brand was cited in per run date — ${targetBrand} vs top competitors`}
            rows={visibilityRows}
            series={brandSeries}
            exportName={`AlexaVisibility_VisibilityByDate_${targetBrand}`}
            onDownloadPng={handleDownloadPng}
          />
          <TrendCard
            id='trend-soar'
            title='SOAR — share of top-3 recommendation slots'
            subtitle='% of all Alexa top-3 product slots held by each brand per run date — your headline visibility metric'
            rows={soarRows}
            series={brandSeries}
            exportName={`AlexaVisibility_SOAR_${targetBrand}`}
            onDownloadPng={handleDownloadPng}
          />
          <ProductTrendsSection
            id='trend-products'
            targetBrand={targetBrand}
            results={scoped}
            groups={productGroups}
            getBrandColor={getBrandColor}
            onDownloadPng={handleDownloadPng}
          />
          <TrendRecap
            targetBrand={targetBrand}
            windows={windows}
            monthOverMonth={monthOverMonth}
            improvements={improvements}
            declines={declines}
          />
        </div>
      )}
    </div>
  );
};
